"use client";

import { useEffect } from "react";
import "./quality-compliance.css";
import { Plus_Jakarta_Sans } from "next/font/google";

const jakarta = Plus_Jakarta_Sans({
  subsets: ["latin"],
  variable: "--font-jakarta",
  display: "swap",
});

/* =========================================================
   IMAGES (replace any URL with your own when needed)
========================================================= */

const HERO_BG =
  "/assets/images/quality-compliance-hero.jpg";

const CTA_BG = "/assets/images/quality-compliance-cta.jpg";

/* =========================================================
   ICON SYSTEM
========================================================= */

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

function IconShield() {
  return (
    <svg {...iconProps}>
      <path d="M12 3l7 3v5c0 5-3 8.5-7 10-4-1.5-7-5-7-10V6l7-3z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg {...iconProps}>
      <rect x="4" y="10" width="16" height="11" rx="1.5" />
      <path d="M7 10V7a5 5 0 0 1 10 0v3" />
      <path d="M12 14v3" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg {...iconProps}>
      <rect x="5" y="3" width="14" height="18" rx="1" />
      <path d="M9 7h6M9 11h6M9 15h4" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg {...iconProps}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15.5 14a5 5 0 0 1 5.5 5" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg {...iconProps}>
      <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
    </svg>
  );
}

function IconClipboard() {
  return (
    <svg {...iconProps}>
      <rect x="5" y="4" width="14" height="17" rx="1.5" />
      <path d="M9 4V2h6v2M8 9h8M8 13h8M8 17h5" />
    </svg>
  );
}

function IconAlert() {
  return (
    <svg {...iconProps}>
      <path d="M12 3 2.8 20h18.4L12 3z" />
      <path d="M12 9v5M12 17v.5" />
    </svg>
  );
}

function IconDatabase() {
  return (
    <svg {...iconProps}>
      <ellipse cx="12" cy="5" rx="7" ry="3" />
      <path d="M5 5v7c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
      <path d="M5 12v7c0 1.7 3.1 3 7 3s7-1.3 7-3v-7" />
    </svg>
  );
}

function IconGavel() {
  return (
    <svg {...iconProps}>
      <path d="m14 5 5 5" />
      <path d="m11 8 5 5" />
      <path d="m5 14 5-5 5 5-5 5z" />
      <path d="M3 21h12" />
      <path d="m16 3 5 5" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg {...iconProps}>
      <path d="M20 11a8 8 0 0 0-14.8-4L3 10" />
      <path d="M3 4v6h6" />
      <path d="M4 13a8 8 0 0 0 14.8 4L21 14" />
      <path d="M21 20v-6h-6" />
    </svg>
  );
}

/* =========================================================
   TOP FEATURES
========================================================= */

const topFeatures = [
  [
    IconShield,
    "Enterprise Quality Management",
    "Quality standards and delivery assurance",
  ],
  [
    IconLock,
    "Data Security and Privacy",
    "Protecting customer and enterprise information",
  ],
  [
    IconDocument,
    "Regulatory Compliance",
    "Meeting international and local requirements",
  ],
  [IconUsers, "Governance and Risk", "Responsible and accountable practices"],
  [IconStar, "Continuous Improvement", "Raising the bar every day"],
];

/* =========================================================
   CERTIFICATIONS
========================================================= */

const certifications = [
  {
    type: "ISO",
    title: "ISO 9001:2015",
    subtitle: "Quality Management System",
    text: "Certified for consistent processes and quality delivery.",
  },
  {
    type: "ISO",
    title: "ISO 27001:2022",
    subtitle: "Information Security",
    text: "Committed to information security and data protection.",
  },
  {
    type: "CMMI",
    title: "CMMI",
    subtitle: "Service Delivery",
    text: "Process maturity for high-quality service delivery.",
  },
  {
    type: "SAP",
    title: "SAP Gold Partner",
    subtitle: "Enterprise Technology",
    text: "Recognized for enterprise transformation capabilities.",
  },
  {
    type: "DPIT",
    title: "DPIT Recognised",
    subtitle: "Technology Excellence",
    text: "Recognized for innovation and deep-tech capabilities.",
  },
];

/* =========================================================
   GOVERNANCE
========================================================= */

const governanceCards = [
  [
    IconClipboard,
    "Policies and Frameworks",
    "Clear policies, procedures and standards across HR and operations.",
  ],
  [
    IconAlert,
    "Risk Identification and Mitigation",
    "Proactive risk identification and mitigation across projects and operations.",
  ],
  [
    IconDatabase,
    "Data Privacy",
    "Protection of customer and internal data in line with global regulations.",
  ],
  [
    IconGavel,
    "Legal and Regulatory Compliance",
    "Compliance with applicable laws, industry regulations and obligations.",
  ],
  [
    IconRefresh,
    "Business Continuity",
    "Systems and processes designed for resilience and continuity of service.",
  ],
];

/* =========================================================
   DELIVERY (image URLs)
========================================================= */

const deliveryCards = [
  {
    image:
      "/assets/images/Methodologies-and-Accelerators.jpg",
    title: "Methodologies and Accelerators",
    text: "Proven frameworks for faster, better delivery.",
  },
  {
    image:
      "/assets/images/Experienced-Talent.jpg",
    title: "Experienced Talent",
    text: "Certified consultants with real industry experience.",
  },
  {
    image:
      "/assets/images/Project-Governance.jpg",
    title: "Project Governance",
    text: "Structured oversight and transparent reporting.",
  },
  {
    image:
      "/assets/images/Quality-Reviews.jpg",
    title: "Quality Reviews",
    text: "Regular checkpoints across design, build and deployment.",
  },
  {
    image:
      "/assets/images/Customer-Feedback.jpg",
    title: "Customer Feedback",
    text: "Continuous customer feedback for ongoing improvement.",
  },
];

/* =========================================================
   PAGE
========================================================= */

function Arrow() {
  return (
    <span className="qc-btn-arrow" aria-hidden="true">
      →
    </span>
  );
}

export default function QualityCompliancePage() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      ".qc-feature, .qc-cert-card, .qc-governance-card, .qc-delivery-card, .qc-commitment-content"
    );

    if (!("IntersectionObserver" in window)) {
      elements.forEach((element) => {
        element.classList.add("in-view");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -60px 0px",
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <main className={`${jakarta.variable} quality-page`}>
      {/* ================= HERO (full width) ================= */}

      <section
        className="qc-hero"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        <div className="qc-hero-overlay" />

        <div className="qc-hero-inner">
          <div className="qc-hero-copy">
            <h1>
              Trust Has
              <br />
             To Be Earned.
            </h1>

            <p>
              We are committed to the highest standards of quality, security,
              compliance and governance in everything we do — for our
              customers, people and our partners.
            </p>

            <div className="qc-hero-buttons">
              <a href="#commitment" className="qc-primary-btn">
                Our Commitment <Arrow />
              </a>

              <a href="#governance" className="qc-outline-btn">
                <span className="qc-play" aria-hidden="true">
                  ▶
                </span>
                Watch Our Approach
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CONTENT (centered container) ================= */}

      <div className="qc-container">
        {/* TOP FEATURE TABS */}

        <section className="qc-feature-tabs">
          {topFeatures.map(([Icon, title, text]) => {
            const FeatureIcon = Icon;

            return (
              <div className="qc-feature" key={title}>
                <b>
                  <FeatureIcon />
                </b>

                <div>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </div>
              </div>
            );
          })}
        </section>

        {/* CERTIFICATIONS */}

        <section className="qc-section qc-certifications">
          <div className="qc-section-heading">
            <div>
              <span className="qc-eyebrow">QUALITY & CERTIFICATION</span>

              <h2>Our Certifications and Standards</h2>

              <p>
                We adhere to globally recognised standards and frameworks to
                ensure quality, security and compliance.
              </p>
            </div>

            <a href="#">View All Certifications →</a>
          </div>

          <div className="qc-cert-grid">
            {certifications.map((item, index) => (
              <article className="qc-cert-card" key={item.title}>
                <div className={`qc-cert-logo qc-cert-${index}`}>
                  {item.type}
                </div>

                <h3>{item.title}</h3>

                <strong>{item.subtitle}</strong>

                <p>{item.text}</p>

                <a href="#">View Certificate →</a>
              </article>
            ))}
          </div>
        </section>

        {/* GOVERNANCE */}

        <section id="governance" className="qc-section qc-governance">
          <div className="qc-section-heading">
            <div>
              <span className="qc-eyebrow">GOVERNANCE & RISK</span>

              <h2>Governance and Risk Management</h2>

              <p>
                A structured approach to ensure responsible, transparent and
                compliant operations.
              </p>
            </div>
          </div>

          <div className="qc-governance-grid">
            {governanceCards.map(([Icon, title, text]) => {
              const GovernanceIcon = Icon;

              return (
                <article className="qc-governance-card" key={title}>
                  <div className="qc-governance-icon">
                    <GovernanceIcon />
                  </div>

                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>

                </article>
              );
            })}
          </div>
        </section>

        {/* DELIVERY EXCELLENCE */}

        <section className="qc-section qc-delivery">
          <div className="qc-section-heading">
            <div>
              <span className="qc-eyebrow">DELIVERY EXCELLENCE</span>

              <h2>Delivery Quality and Excellence</h2>

              <p>
                Proven methods, tools and people to ensure predictable,
                high-quality outcomes.
              </p>
            </div>

            <a href="#">Learn More →</a>
          </div>

          <div className="qc-delivery-grid">
            {deliveryCards.map((card) => (
              <article className="qc-delivery-card" key={card.title}>
                <div className="qc-delivery-image">
                  <img src={card.image} alt={card.title} loading="lazy" />
                </div>

                <div className="qc-delivery-content">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* COMMITMENT */}

        <section id="commitment" className="qc-commitment">
          <div className="qc-commitment-content">
            <div>
              <span className="qc-eyebrow">OUR COMMITMENT</span>

              <h2>
                Quality is not
                <br />
                <span>a checkpoint.</span>
              </h2>
            </div>

            <p>
              It is embedded into how we think, design, build and deliver.
              Every process, project and customer interaction is an
              opportunity to create better outcomes.
            </p>
          </div>
        </section>
      </div>

      {/* ================= BOTTOM CTA (full width) ================= */}

      <section
        className="qc-bottom-cta"
        style={{ backgroundImage: `url(${CTA_BG})` }}
      >
        <div className="qc-cta-inner">
          <div>
            <span className="qc-eyebrow">QUALITY. SECURITY. GOVERNANCE.</span>

            <h2>
              Do More.
              <br />
              <strong>Together.</strong>
            </h2>

            <p>
              High standards. Greater confidence. Real progress for our
              customers.
            </p>
          </div>

          <a className="qc-white-btn" href="/tellusyourproblem#tell-details">
            Talk to Our Team <Arrow />
          </a>
        </div>
      </section>
    </main>
  );
}